import { Box, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { EmailVerificationType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserEmailVerificationWebApiHandler_getUnverifiedEmailListings,
  useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import propertyUtils from '../../../utils/propertyUtils';

import { useAuth } from '../../AuthProvider';

import type { ListingDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const failedVerificationMessage =
  'This account doesn’t match account provided on listing. For additional help or questions, contact customer support.';
const needVerificationMessage = 'We found additional listings associated with your email address.';

type Props = {
  handleResendEmailClick: (notificationText: string) => void;
  failedVerification: boolean;
  onClose: () => void;
};

const VerifyEmailModal = ({ handleResendEmailClick, failedVerification, onClose }: Props): JSX.Element => {
  const { user } = useAuth();
  const email = user?.email || '';
  const isPostingPathAdmin = user?.isPostingPathAdmin ?? false;

  const { data: { activeListings = [], inactiveListings = [] } = {} } =
    useRentalManagerUserEmailVerificationWebApiHandler_getUnverifiedEmailListings({
      limit: 5,
    });

  const { mutateAsync: sendSecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken();

  const notificationText = `Email resent. The additional listings associated with your account will be added once you
              verify your email at ${email}.`;

  const onResendEmailClick = async () => {
    if (isPostingPathAdmin) {
      handleResendEmailClick(notificationText);
      onClose();
    } else {
      try {
        await sendSecurityToken({
          getParams: { emailVerificationType: EmailVerificationType.ACCOUNT_EMAIL_CLAIM_LISTINGS },
        });
        handleResendEmailClick(notificationText);
        onClose();
      } catch (error) {
        logError({
          error,
          context: 'onResendEmailClick',
        });
      }
    }
  };

  const renderAddress = (listing: ListingDto, i: number) => {
    const { formattedCompleteAddress } = propertyUtils.formatAddress({
      street: listing.street,
      city: listing.city,
      state: listing.state,
      zip: listing.zip,
      unit: listing.unit,
      name: listing.name,
    });
    return (
      <Box css={{ marginBlock: '200' }} key={i} data-testid="address-list-item">
        <Text asChild>
          <p>{formattedCompleteAddress}</p>
        </Text>
      </Box>
    );
  };

  return (
    <Modal
      header={<Heading level={2}>{`${failedVerification ? 'Incorrect account' : 'Listings found'}`}</Heading>}
      body={
        <Box css={{ paddingBottom: '400' }}>
          <Text>{failedVerification ? failedVerificationMessage : needVerificationMessage}</Text>
          {!failedVerification && (
            <>
              <Box css={{ marginBlock: '400' }}>
                <Text data-testid="email-text">
                  We&apos;ve sent an email to <b>{email}</b>. Select the link in the email to confirm your email address
                  and access those listings in Zillow Rental Manager.
                  <TextButton onClick={onResendEmailClick} textStyle="body">
                    Resend email
                  </TextButton>
                </Text>
              </Box>
              {activeListings.length > 0 && (
                <Box css={{ marginBlock: '200' }}>
                  <Text textStyle="body-bold" data-testid="active-listings" asChild>
                    <p>Active Listings</p>
                  </Text>
                  {activeListings.map((listing: ListingDto, i: number) => renderAddress(listing, i))}
                </Box>
              )}

              {inactiveListings.length > 0 && (
                <Box css={{ marginBlock: '200' }}>
                  <Text data-testid="inactive-listings" textStyle="body-bold" asChild>
                    <p>Inactive Listings</p>
                  </Text>
                  {inactiveListings.map((listing: ListingDto, i: number) => renderAddress(listing, i))}
                </Box>
              )}
            </>
          )}
        </Box>
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default VerifyEmailModal;
